import { QueryMetricsRequest } from "@/shared/domain/metrics";
import type { Condition } from "@/types";

/** A time bucket as carried on chart data (Unix-epoch nanoseconds). `bigint`
 *  so the clicked bucket's bounds reach the drill-down query at full ns
 *  precision instead of a rounded `number`. */
export interface BucketWindow {
  start_time: bigint;
  end_time: bigint;
}

/**
 * Page size for the drill-down dialog's paginated query.
 *
 * TODO(dave): Raise this page size limit & add a result count.
 * The backend can easily fetch these pages as an index-only scan, so
 * we can do all that easily enough.
 */
export const DRILLDOWN_PAGE_SIZE = 25;

/**
 * Build a raw-records query for the records that fall inside one aggregate
 * bucket. Used by the drill-down dialog. The window is bounded to the single
 * bucket and paginated at `maxResults`.
 */
export function bucketToQueryRequest(args: {
  name: string;
  bucket: BucketWindow;
  maxResults?: number;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Filter conditions */
  condition?: Condition;
}): QueryMetricsRequest {
  const {
    name,
    bucket,
    maxResults = DRILLDOWN_PAGE_SIZE,
    deviceIds,
    sessionIds,
    condition,
  } = args;
  return {
    name,
    time_filter: "end_time",
    start_time_ns: bucket.start_time,
    end_time_ns: bucket.end_time,
    max_results: maxResults,
    include_device_ids: deviceIds,
    include_session_ids: sessionIds,
    condition,
  };
}

/**
 * The window that contains exactly the instant `ns`, and nothing else.
 *
 * A raw chart point is one record at one timestamp, not a calendar bucket, so
 * it has no width of its own — and `{start: ns, end: ns}` is empty under the
 * half-open `>= start AND < end` filter the records query applies, matching
 * nothing at all. One nanosecond is the smallest window the timestamps can
 * distinguish, so `[ns, ns + 1)` holds that record and no neighbour.
 */
export function instantWindow(ns: bigint): BucketWindow {
  return { start_time: ns, end_time: ns + 1n };
}

/**
 * The window that contains every point of `selection`, and the sessions behind
 * them.
 *
 * A grouped raw click resolves one point per group, each at its own moment
 * (see `nearestPerGroup`), so no single instant holds them and
 * {@link instantWindow} would return a window containing only one. The bounds
 * here are wide enough for all of them; `sessionIds` is what keeps the query
 * to exactly those rows rather than everything else that happens to fall
 * between them. Together they make the drill-down list precisely what the
 * tooltip named.
 *
 * `undefined` when nothing in the selection carries a session — the caller has
 * nothing to drill into.
 */
export function selectionWindow(
  selection: readonly { start_time: bigint; session_id?: string }[],
): { bucket: BucketWindow; sessionIds: string[] } | undefined {
  const sessionIds = selection
    .map((point) => point.session_id)
    .filter((id): id is string => id !== undefined);
  if (sessionIds.length === 0) {
    return undefined;
  }
  const times = selection.map((point) => point.start_time);
  const start = times.reduce((a, b) => (b < a ? b : a));
  const end = times.reduce((a, b) => (b > a ? b : a));
  return { bucket: { start_time: start, end_time: end + 1n }, sessionIds };
}
